'use client';

import { useCallback, useRef } from 'react';

const SESSION_CACHE_KEY = 'fp_vid';
const SESSION_CONFIDENCE_KEY = 'fp_confidence';

const FINGERPRINT_DISABLED =
  process.env.NEXT_PUBLIC_PAYWALL_FINGERPRINT_DISABLED === 'true';

export interface FingerprintResult {
  visitorId: string;
  confidenceScore: number | null;
}

function generateDevVisitorId(): string {
  const suffix = Math.random().toString(36).slice(2, 10);
  return `devvisitor${suffix}`;
}

interface FpAgent {
  get: () => Promise<{
    visitorId: string;
    confidence: { score: number };
  }>;
}

/**
 * Lazy FingerprintJS Pro hook.
 *
 * - When NEXT_PUBLIC_PAYWALL_FINGERPRINT_DISABLED=true: returns a mock visitorId without
 *   calling the FingerprintJS API (saves free tier quota).
 * - Only loads the SDK when `getFingerprint()` is first called (saves API calls).
 * - Caches the result in sessionStorage for the duration of the browser session.
 * - Uses EU region.
 */
export function useFingerprint() {
  const agentRef = useRef<Promise<FpAgent> | null>(null);

  const getFingerprint = useCallback(async (): Promise<FingerprintResult> => {
    if (typeof window === 'undefined') {
      throw new Error('useFingerprint can only be used in the browser');
    }

    const cachedId = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (cachedId) {
      const cachedScore = sessionStorage.getItem(SESSION_CONFIDENCE_KEY);
      return {
        visitorId: cachedId,
        confidenceScore: cachedScore ? parseFloat(cachedScore) : null,
      };
    }

    if (FINGERPRINT_DISABLED) {
      const mockId = generateDevVisitorId();
      sessionStorage.setItem(SESSION_CACHE_KEY, mockId);
      return { visitorId: mockId, confidenceScore: null };
    }

    if (!agentRef.current) {
      const apiKey = process.env.NEXT_PUBLIC_FINGERPRINT_API_KEY;
      if (!apiKey) {
        throw new Error('NEXT_PUBLIC_FINGERPRINT_API_KEY is not set');
      }

      agentRef.current = import('@fingerprintjs/fingerprintjs-pro').then(
        (FingerprintJS) =>
          FingerprintJS.load({
            apiKey,
            region: 'eu',
            endpoint: 'https://www.odciskpalca.lekolizja.pl',
            scriptUrlPattern:
              'https://www.odciskpalca.lekolizja.pl/web/v<version>/<apiKey>/loader_v<loaderVersion>.js',
          }),
      );
    }

    const agent = await agentRef.current;
    const result = await agent.get();
    const confidenceScore = result.confidence?.score ?? null;

    sessionStorage.setItem(SESSION_CACHE_KEY, result.visitorId);
    if (confidenceScore != null) {
      sessionStorage.setItem(SESSION_CONFIDENCE_KEY, String(confidenceScore));
    }

    return { visitorId: result.visitorId, confidenceScore };
  }, []);

  return { getFingerprint };
}
